/**
 * CelluleForm — modale de création / édition d'une cellule.
 */
import { useState, useEffect, useMemo } from "react";
import Modal from "./Modal";
import Input from "./Input";
import Select from "./Select";
import Button from "./Button";
import { colors } from "../styles/tokens";
import useMembers from "../hooks/useMembers";

const EMPTY = {
  nom: "",
  description: "",
  eglise_id: "",
  responsable_id: "",
};

export default function CelluleForm({
  open,
  onClose,
  onSubmit,
  onDelete,
  eglises = [],
  initial = null,
}) {
  const { members } = useMembers();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        nom: initial.nom || "",
        description: initial.description || "",
        eglise_id: initial.eglise_id ? String(initial.eglise_id) : "",
        responsable_id: initial.responsable_id ? String(initial.responsable_id) : "",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setErrors({});
    setGlobalError(null);
  }, [open, initial]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: null }));
    // Si on change d'église, on réinitialise le responsable (membre d'une autre église)
    if (key === "eglise_id") {
      setForm((prev) => ({ ...prev, eglise_id: value, responsable_id: "" }));
    }
  };

  /** Membres éligibles comme responsables (même église). */
  const responsableOptions = useMemo(() => {
    if (!form.eglise_id) return [];
    return members
      .filter((m) => m.eglise_id === Number(form.eglise_id))
      .map((m) => ({
        value: String(m.id),
        label: `${m.prenom} ${m.nom}${m.role_eglise ? ` — ${m.role_eglise}` : ""}`,
      }));
  }, [members, form.eglise_id]);

  const validate = () => {
    const errs = {};
    if (!form.nom.trim())  errs.nom = "Le nom est obligatoire";
    if (!form.eglise_id)   errs.eglise_id = "Veuillez sélectionner une église";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setGlobalError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        description: form.description.trim() || null,
        eglise_id: Number(form.eglise_id),
        responsable_id: form.responsable_id ? Number(form.responsable_id) : null,
      };
      const result = await onSubmit(payload);
      if (result?.ok === false) {
        setGlobalError(result.error || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      setGlobalError(err.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!initial) return;
    if (!window.confirm(`Supprimer la cellule « ${initial.nom} » ?\nLes ${initial.nb_membres || 0} membre(s) seront détaché(s).`)) return;
    setSubmitting(true);
    try {
      await onDelete?.(initial);
    } finally {
      setSubmitting(false);
    }
  };

  const eglisesOptions = eglises.map((e) => ({
    value: String(e.id),
    label: `${e.nom}${e.ville ? ` — ${e.ville}` : ""}`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={initial ? "Modifier la cellule" : "Nouvelle cellule"}
      subtitle={initial
        ? `Modification de « ${initial.nom} »`
        : "Créez un groupe de maison ou une équipe."}
      size="md"
      footer={
        <>
          {initial && onDelete && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={submitting}
              style={{ marginRight: "auto" }}
            >
              🗑 Supprimer la cellule
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {initial ? "Sauvegarder" : "Créer la cellule"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>
            {globalError}
          </div>
        )}

        <Input
          label="Nom de la cellule"
          value={form.nom}
          onChange={set("nom")}
          placeholder="Ex : Cellule Riviera, Groupe Jeunesse…"
          required
          error={errors.nom}
        />

        <Input
          label="Description"
          value={form.description}
          onChange={set("description")}
          placeholder="Décrivez le groupe (optionnel)"
        />

        <Select
          label="Église"
          value={form.eglise_id}
          onChange={set("eglise_id")}
          options={eglisesOptions}
          required
          error={errors.eglise_id}
          placeholder="Sélectionnez une église..."
        />

        <Select
          label="Responsable (optionnel)"
          value={form.responsable_id}
          onChange={set("responsable_id")}
          options={responsableOptions}
          placeholder={
            form.eglise_id
              ? (responsableOptions.length === 0 ? "Aucun membre dans cette église" : "Aucun")
              : "Sélectionnez d'abord une église"
          }
          disabled={!form.eglise_id || responsableOptions.length === 0}
        />

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
